import { LegacyColumnDef } from '@tanstack/react-table/legacy';
import { useMemo } from 'react';
import { MdArrowDropDown, MdArrowRight, MdSwapHoriz } from 'react-icons/md';
import { Checkbox } from '@Components/FormCheck';
import { IconButton } from '@Components/IconButton';
import { Appearance, Design, Size } from '@Components/designConstants';
import {
  ColumnHeader,
  createMultiSelectFilter,
  createNumberRangeFilter,
  hierarchicalDateSort,
  hierarchicalNumericSort
} from '../HierarchicalTable';
import {
  GroupLevelValues,
  TableRow,
  isEditableAllocationRow,
  isGroupRow,
  isPerformanceAllocationRow
} from './post-optimization-constants';
import { postOptimizationConfig } from './post-optimization-config';
import { PostOptimizationEditState } from './usePostOptimizationEdits';

// Create filter function instances using the shared config
const multiSelectFilter = createMultiSelectFilter(postOptimizationConfig);
const numberRangeFilter = createNumberRangeFilter(postOptimizationConfig);

const CURRENCY_WHOLE = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

const CURRENCY_CENTS = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const NUMBER_TWO_DECIMALS = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const formatCurrency = (value: number | undefined, decimals: 0 | 2 = 0): string =>
  value === undefined ? '-' : (decimals === 0 ? CURRENCY_WHOLE : CURRENCY_CENTS).format(value);

const formatNumber = (value: number | undefined): string =>
  value === undefined ? '-' : NUMBER_TWO_DECIMALS.format(value);

/**
 * Checkbox column for the Must Include / Must Exclude flags.
 * The flags are mutually exclusive: checking one clears the other.
 */
const createMustFlagColumn = (
  field: 'mustInclude' | 'mustExclude',
  label: string,
  onUpdateEdit: (row: TableRow, edits: PostOptimizationEditState) => void
): LegacyColumnDef<TableRow> => ({
  accessorKey: field,
  header: label,
  cell: ({ row, getValue }) => {
    if (!isEditableAllocationRow(row.original)) {
      return null;
    }
    const checked = getValue<boolean>() || false;
    const showsBudgetReallocation =
      field === 'mustExclude' && checked && row.original.shouldReallocateBudget;
    return (
      <div className='checkbox-cell'>
        <span className='checkbox-with-indicator'>
          <Checkbox
            checked={checked}
            onChange={() => {
              const next = !checked;
              onUpdateEdit(
                row.original,
                field === 'mustInclude'
                  ? { mustInclude: next, mustExclude: next ? false : row.original.mustExclude }
                  : { mustExclude: next, mustInclude: next ? false : row.original.mustInclude }
              );
            }}
          />
          {showsBudgetReallocation && (
            <span
              className='budget-reallocated-indicator'
              role='img'
              aria-label='Excluded budget reallocated proportionally'
              title='Excluded budget reallocated proportionally'
            >
              <MdSwapHoriz aria-hidden='true' size={14} />
            </span>
          )}
        </span>
      </div>
    );
  }
});

export const usePostOptimizationColumns = (
  onUpdateEdit: (row: TableRow, edits: PostOptimizationEditState) => void
): LegacyColumnDef<TableRow>[] =>
  useMemo<LegacyColumnDef<TableRow>[]>(
    () => [
      {
        id: 'expander',
        header: () => null,
        cell: ({ row }) =>
          row.getCanExpand() ? (
            <IconButton
              appearance={Appearance.EMPTY}
              design={Design.NEUTRAL}
              size={Size.SM}
              className='expander-btn'
              onClick={row.getToggleExpandedHandler()}
            >
              {row.getIsExpanded() ? <MdArrowDropDown size={20} /> : <MdArrowRight size={20} />}
            </IconButton>
          ) : null
      },
      {
        accessorKey: 'networkContent' satisfies keyof GroupLevelValues,
        header: ({ column }) => <ColumnHeader column={column} label='Network + Content' />,
        cell: ({ row, getValue }) => {
          if (!isGroupRow(row.original)) {
            return null;
          }
          return <div className='bold-cell'>{getValue<string>()}</div>;
        },
        filterFn: multiSelectFilter
      },
      {
        accessorKey: 'format' satisfies keyof GroupLevelValues,
        header: ({ column }) => <ColumnHeader column={column} label='Format' />,
        cell: ({ row, getValue }) => {
          if (!isGroupRow(row.original)) {
            return null;
          }
          return getValue<string>() || '';
        },
        filterFn: multiSelectFilter
      },
      {
        accessorKey: 'exploitExplore' satisfies keyof GroupLevelValues,
        header: ({ column }) => <ColumnHeader column={column} label='Exploit / Explore' />,
        cell: ({ row, getValue }) => {
          if (!isGroupRow(row.original)) {
            return null;
          }
          return <div className='bold-cell'>{getValue<string>() || ''}</div>;
        },
        filterFn: multiSelectFilter
      },
      {
        accessorKey: 'placement',
        header: ({ column }) => <ColumnHeader column={column} label='Placement' />,
        cell: ({ getValue }) => getValue() || '',
        filterFn: multiSelectFilter
      },
      {
        accessorKey: 'lastLive',
        header: ({ column }) => <ColumnHeader column={column} label='Last Live' />,
        cell: ({ getValue }) => getValue<string>() || '',
        sortFn: hierarchicalDateSort,
        filterFn: multiSelectFilter
      },
      {
        accessorKey: 'allocationMetric',
        header: ({ column }) => (
          <ColumnHeader
            column={column}
            label='Allocation Metric'
            filterType='range'
            displayDecimals={2}
          />
        ),
        cell: ({ row, getValue }) => {
          if (!isPerformanceAllocationRow(row.original)) {
            return null;
          }
          const value = getValue<number | 'no data'>();
          return value === 'no data' ? 'no data' : formatNumber(value);
        },
        sortFn: hierarchicalNumericSort,
        filterFn: numberRangeFilter
      },
      createMustFlagColumn('mustInclude', 'Must Include', onUpdateEdit),
      createMustFlagColumn('mustExclude', 'Must Exclude', onUpdateEdit),
      {
        accessorKey: 'finalAllocation',
        header: ({ column }) => (
          <ColumnHeader
            column={column}
            label='Final Allocation'
            filterType='range'
            displayDecimals={0}
          />
        ),
        cell: ({ row, getValue }) => {
          if (!isEditableAllocationRow(row.original)) {
            return null;
          }
          return formatCurrency(getValue<number>());
        },
        sortFn: hierarchicalNumericSort,
        filterFn: numberRangeFilter
      },
      {
        accessorKey: 'lineItemCpm',
        header: ({ column }) => (
          <ColumnHeader column={column} label='CPM' filterType='range' displayDecimals={2} />
        ),
        cell: ({ getValue }) => {
          const value = getValue<number>();
          return value == null ? null : formatCurrency(value, 2);
        },
        sortFn: hierarchicalNumericSort,
        filterFn: numberRangeFilter
      },
      {
        accessorKey: 'aggregateCpm' satisfies keyof GroupLevelValues,
        header: ({ column }) => (
          <ColumnHeader
            column={column}
            label='CPM'
            displayLabel={<span className='overline'>CPM</span>}
            filterType='range'
            displayDecimals={2}
          />
        ),
        cell: ({ getValue }) => {
          const value = getValue<number>();
          return value == null ? null : formatCurrency(value, 2);
        },
        sortFn: hierarchicalNumericSort,
        filterFn: numberRangeFilter
      },
      {
        accessorKey: 'budget' satisfies keyof GroupLevelValues,
        header: ({ column }) => (
          <ColumnHeader column={column} label='Budget' filterType='range' displayDecimals={0} />
        ),
        cell: ({ row, getValue }) => {
          if (!isGroupRow(row.original)) {
            return null;
          }
          return formatCurrency(getValue<number>());
        },
        sortFn: hierarchicalNumericSort,
        filterFn: numberRangeFilter
      }
    ],
    [onUpdateEdit]
  );
